import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { ZoomImage } from '../components/ZoomImage';
import { useAuth } from '../hooks/useAuth';

interface TodayResponse {
  puzzle: { id: string; puzzleNumber: number };
  imageUrl: string;
  currentZoomLevel: number;
  run: { id: string; status: string; dezoomsUsed?: number } | null;
  hasOfficialRun: boolean;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
  shareText: string | null;
  durationSec: number | null;
}

export function PlayPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, guest, loginGuest, loading: authLoading } = useAuth();
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [imageUrl, setImageUrl] = useState('');
  const [dezooms, setDezooms] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);

  const init = useCallback(async () => {
    if (params.get('guest') === '1' && !user && !guest) {
      await loginGuest();
    }
    const data = await api<TodayResponse>('/puzzles/today');
    setToday(data);
    setZoomLevel(data.currentZoomLevel);
    setImageUrl(data.imageUrl);

    if (data.run) {
      setDezooms(data.run.dezoomsUsed ?? 0);
      if (data.run.status !== 'in_progress') {
        navigate('/result', { state: { fromRun: data.run.id } });
        return;
      }
      setRunId(data.run.id);
    }
  }, [params, user, guest, loginGuest, navigate]);

  useEffect(() => {
    if (authLoading) return;
    init().catch((e) => setError(e.message));
  }, [authLoading, init]);

  const ensureRun = async () => {
    if (runId) return runId;
    const res = await api<{ runId: string; startedAt: string }>('/runs/start', { method: 'POST' });
    setRunId(res.runId);
    setStartedAt(new Date(res.startedAt).getTime());
    return res.runId;
  };

  const handleGuess = async (guess: string) => {
    setBusy(true);
    setError('');
    try {
      const id = await ensureRun();
      const res = await api<GuessResponse>(`/runs/${id}/guess`, {
        method: 'POST',
        body: JSON.stringify({ guess }),
      });

      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);

      const zoomRes = await api<TodayResponse>('/puzzles/today');
      const levelUrl = zoomRes.imageUrl;
      setImageUrl(levelUrl);

      if (res.status !== 'in_progress') {
        navigate('/result', {
          state: {
            won: res.status === 'won',
            score: res.score ?? 0,
            dezoomsUsed: res.dezoomsUsed,
            durationSec: res.durationSec ?? 0,
            shareText: res.shareText,
            puzzleNumber: today?.puzzle.puzzleNumber ?? 0,
          },
        });
      }
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  if (!today) {
    return <main className="page play"><p className="loading">{error || 'Chargement…'}</p></main>;
  }

  return (
    <main className="page play">
      <header className="play-header">
        <span className="puzzle-num">#{today.puzzle.puzzleNumber}</span>
        <DezoomDots used={dezooms} />
      </header>
      <ZoomImage src={imageUrl} zoomKey={zoomLevel} />
      {error && <p className="error" role="alert">{error}</p>}
      <GuessForm onSubmit={handleGuess} disabled={busy} />
      {startedAt && (
        <p className="timer" aria-live="polite">
          <Timer since={startedAt} />
        </p>
      )}
    </main>
  );
}

function Timer({ since }: { since: number }) {
  const [sec, setSec] = useState(Math.floor((Date.now() - since) / 1000));
  useEffect(() => {
    const id = setInterval(() => setSec(Math.floor((Date.now() - since) / 1000)), 1000);
    return () => clearInterval(id);
  }, [since]);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return <>{m}:{s.toString().padStart(2, '0')}</>;
}
